import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  where,
  addDoc,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import { ListSkeleton } from "../components/LoadingSkeleton";
import FloatingActionButton from "../components/FloatingActionButton";
import { decryptPatient } from "../utils/encryption";

const statusColor = (status) => {
  if (!status) return "#06b6d4"; // Primary teal
  const s = String(status).toLowerCase();
  if (s.includes("active")) return "#10b981"; // Green
  if (s.includes("recover")) return "#64748b"; // Muted gray
  if (s.includes("new") || s.includes("pending")) return "#f59e0b"; // Warning orange
  return "#06b6d4"; // Default to primary teal
};

function initialFromName(name, fallback = "-") {
  if (!name || typeof name !== "string") return fallback;
  return name.trim().charAt(0).toUpperCase() || fallback;
}

function PatientsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(location.state?.filterStatus || "All");
  const [q, setQ] = useState("");
  const [searchMode, setSearchMode] = useState(false);
  const searchInputRef = useRef(null);
  const [notificationCount, setNotificationCount] = useState(0);
  const [showFormLinkModal, setShowFormLinkModal] = useState(false);
  const [formLink, setFormLink] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [showPatientGuide, setShowPatientGuide] = useState(false);
  const [newPatientId, setNewPatientId] = useState(null);

  useEffect(() => {
    // Check URL for guide parameter
    const params = new URLSearchParams(location.search);
    const guideParam = params.get("guide");
    const newPatientParam = params.get("newPatient");

    if (guideParam === "true" && newPatientParam) {
      setShowPatientGuide(true);
      setNewPatientId(newPatientParam);
    }
  }, [location.search]);

  useEffect(() => {
    const load = async (user) => {
      try {
        if (!user) {
          setLoading(false);
          setPatients([]);
          setNotificationCount(0);
          return;
        }
        const snap = await getDocs(
          query(collection(db, "patients"), where("userId", "==", user.uid)),
        );
        const data = snap.docs.map((d) =>
          decryptPatient({ id: d.id, ...d.data() }),
        );
        // Sort by createdAt locally
        data.sort((a, b) => {
          const aTime = a.createdAt?.toMillis() || 0;
          const bTime = b.createdAt?.toMillis() || 0;
          return bTime - aTime;
        });
        setPatients(data);

        // Load form link (only the most recent active one)
        const formLinksSnap = await getDocs(
          query(
            collection(db, "patientFormLinks"),
            where("userId", "==", user.uid),
            where("isActive", "==", true),
          ),
        );
        const linksData = formLinksSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        if (linksData.length > 0) {
          // Get the most recent one
          linksData.sort((a, b) => {
            const aTime = a.createdAt?.toMillis() || 0;
            const bTime = b.createdAt?.toMillis() || 0;
            return bTime - aTime;
          });
          setFormLink(linksData[0]);
        } else {
          setFormLink(null);
        }

        // Fetch notification count
        const notifSnap = await getDocs(
          query(
            collection(db, "notifications"),
            where("userId", "==", user.uid),
          ),
        );
        // Count unread only for the latest notification per appointment
        const latestByKey = new Map();
        notifSnap.docs.forEach((d) => {
          const n = { id: d.id, ...d.data() };
          const key = n.appointmentId || n.id;
          const time = n.createdAt?.toMillis ? n.createdAt.toMillis() : 0;
          const prev = latestByKey.get(key);
          const prevTime = prev?.createdAt?.toMillis
            ? prev.createdAt.toMillis()
            : 0;
          if (!prev || time >= prevTime) {
            latestByKey.set(key, n);
          }
        });
        const unreadCount = Array.from(latestByKey.values()).filter(
          (n) => n.read === false,
        ).length;
        setNotificationCount(unreadCount);
      } catch (e) {
        console.warn("Failed to fetch patients", e);
      } finally {
        setLoading(false);
      }
    };

    // Wait for auth state to be determined (handles page refresh)
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      load(user);
    });

    // Refresh notification count when page becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const currentUser = auth.currentUser;
        if (currentUser) {
          load(currentUser);
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      unsubscribe();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const activeCount = useMemo(
    () =>
      patients.filter((p) =>
        String(p.status || "")
          .toLowerCase()
          .includes("active"),
      ).length || patients.length,
    [patients],
  );

  const filtered = useMemo(() => {
    const queryLower = q.trim().toLowerCase();
    return patients.filter((p) => {
      const matchesQuery =
        !queryLower ||
        String(p.name || "")
          .toLowerCase()
          .includes(queryLower) ||
        String(p.condition || p.reason || p.info || "")
          .toLowerCase()
          .includes(queryLower);
      if (!matchesQuery) return false;
      if (filter === "All") return true;
      const s = String(p.status || "").toLowerCase();
      if (filter === "Active") return s.includes("active");
      if (filter === "New") return s.includes("new") || s.includes("pending");
      if (filter === "Recovered") return s.includes("recover");
      return true;
    });
  }, [patients, filter, q]);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const getFilterCount = (filterName) => {
    if (filterName === "All") return patients.length;
    return patients.filter((p) => {
      const s = String(p.status || "").toLowerCase();
      if (filterName === "Active") return s.includes("active");
      if (filterName === "New")
        return s.includes("new") || s.includes("pending");
      if (filterName === "Recovered") return s.includes("recover");
      return false;
    }).length;
  };

  const getPaginatedPatients = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginated = filtered.slice(startIndex, endIndex);
    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    return { paginated, totalPages, totalItems: filtered.length };
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, q]);

  useEffect(() => {
    if (searchMode && searchInputRef.current) {
      // focus the input when search mode is enabled
      setTimeout(() => searchInputRef.current.focus(), 50);
    }
  }, [searchMode]);

  const generateFormLink = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    // If link already exists, just show the modal
    if (formLink) {
      setShowFormLinkModal(true);
      return;
    }

    setGeneratingLink(true);
    try {
      // Generate unique token
      const token = `${currentUser.uid}_${Date.now()}_${Math.random()
        .toString(36)
        .substring(2, 9)}`;

      const formLinkRef = await addDoc(collection(db, "patientFormLinks"), {
        userId: currentUser.uid,
        token: token,
        createdAt: serverTimestamp(),
        usageCount: 0,
        isActive: true,
      });

      const newFormLink = {
        id: formLinkRef.id,
        token: token,
        createdAt: { toMillis: () => Date.now() },
        usageCount: 0,
        isActive: true,
      };

      setFormLink(newFormLink);
      setShowFormLinkModal(true);
    } catch (e) {
      console.error("Failed to generate form link", e);
    } finally {
      setGeneratingLink(false);
    }
  };

  const createNewFormLink = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    setGeneratingLink(true);
    try {
      // Deactivate existing link if it exists
      if (formLink && formLink.id) {
        await updateDoc(doc(db, "patientFormLinks", formLink.id), {
          isActive: false,
        });
      }

      // Generate unique token
      const token = `${currentUser.uid}_${Date.now()}_${Math.random()
        .toString(36)
        .substring(2, 9)}`;

      const formLinkRef = await addDoc(collection(db, "patientFormLinks"), {
        userId: currentUser.uid,
        token: token,
        createdAt: serverTimestamp(),
        usageCount: 0,
        isActive: true,
      });

      const newFormLink = {
        id: formLinkRef.id,
        token: token,
        createdAt: { toMillis: () => Date.now() },
        usageCount: 0,
        isActive: true,
      };

      setFormLink(newFormLink);
    } catch (e) {
      console.error("Failed to create new form link", e);
    } finally {
      setGeneratingLink(false);
    }
  };

  const copyFormLink = () => {
    if (!formLink) return;
    const baseUrl = window.location.origin;
    const formUrl = `${baseUrl}/patient-form/${formLink.token}`;
    navigator.clipboard.writeText(formUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const shareFormLink = async () => {
    if (!formLink) return;
    const baseUrl = window.location.origin;
    const formUrl = `${baseUrl}/patient-form/${formLink.token}`;

    // Check if Web Share API is available
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Patient Registration Form",
          text: "Please fill out this form to register as a patient.",
          url: formUrl,
        });
      } catch (err) {
        // User cancelled or error occurred, fallback to copy
        if (err.name !== "AbortError") {
          console.error("Error sharing:", err);
          copyFormLink();
        }
      }
    } else {
      // Fallback to copy if Web Share API is not available
      copyFormLink();
    }
  };

  const getFormLinkUrl = () => {
    if (!formLink) return "";
    const baseUrl = window.location.origin;
    return `${baseUrl}/patient-form/${formLink.token}`;
  };

  const { paginated, totalPages, totalItems } = getPaginatedPatients();

  return (
    <div className="pts-layout-new">
      {/* Fixed Header Section */}
      <div className="pts-fixed-header">
        <header className="pts-header-new">
          <div className="pts-header-container">
            <div className="pts-header-content">
              {!searchMode ? (
                <>
                  <div>
                    <h1>Patients</h1>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      alignItems: "center",
                    }}
                  >
                    <button
                      className="pts-notification-btn"
                      aria-label="Search"
                      onClick={() => setSearchMode(true)}
                      title="Search patients"
                      style={{
                        background: "transparent",
                        border: "none",
                        width: "40px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                    >
                      <span className="material-symbols-outlined">search</span>
                    </button>

                    <button
                      className="pts-notification-btn"
                      aria-label="Form Link"
                      onClick={generateFormLink}
                      disabled={generatingLink}
                      style={{
                        background: "var(--register-primary)",
                        color: "white",
                        border: "none",
                        borderRadius: "12px",
                        padding: "10px",
                        width: "40px",
                        height: "40px",
                        cursor: generatingLink ? "not-allowed" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: generatingLink ? 0.7 : 1,
                      }}
                      title="Generate Patient Form Link"
                    >
                      <span
                        className="material-symbols-outlined"
                        style={{
                          fontSize: "20px",
                          display: "flex",
                          alignItems: "center",
                          lineHeight: "1",
                        }}
                      >
                        {generatingLink ? "hourglass_empty" : "link"}
                      </span>
                    </button>
                    <button
                      onClick={() => navigate("/notifications")}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "8px",
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        color: "#64748b",
                      }}
                    >
                      <span className="material-symbols-outlined">
                        notifications
                      </span>
                      {notificationCount > 0 && (
                        <span
                          style={{
                            position: "absolute",
                            top: "4px",
                            right: "4px",
                            background: "#ef4444",
                            color: "white",
                            borderRadius: "10px",
                            padding: "2px 6px",
                            fontSize: "10px",
                            fontWeight: 600,
                            minWidth: "16px",
                            textAlign: "center",
                          }}
                        >
                          {notificationCount}
                        </span>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    width: "100%",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <input
                      ref={searchInputRef}
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Search by name or condition..."
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "12px",
                        border: "1px solid var(--register-border)",
                        fontSize: "14px",
                      }}
                    />
                  </div>
                  <button
                    onClick={() => {
                      setSearchMode(false);
                      setQ("");
                    }}
                    aria-label="Close search"
                    title="Close search"
                    style={{
                      background: "transparent",
                      border: "none",
                      fontSize: "20px",
                      cursor: "pointer",
                      color: "var(--register-text)",
                    }}
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>

            {/* Fixed Controls Section */}
            {!searchMode && (
              <div className="pts-controls">
                <div className="pts-filters-section">
                  <div className="pts-filters-new no-scrollbar">
                    {["All", "Active", "New", "Recovered"].map((f) => {
                      const count = getFilterCount(f);
                      return (
                        <button
                          key={f}
                          className={`pts-filter-chip ${
                            filter === f ? "active" : ""
                          }`}
                          onClick={() => setFilter(f)}
                        >
                          {f}
                          {count > 0 && (
                            <span className="pts-filter-count">{count}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </header>
      </div>

      {/* Scrollable List Container */}
      <div className="pts-scroll-container">
        <div className="pts-content-wrapper">
          <main className="pts-list-content">
            {loading ? (
              <ListSkeleton count={5} />
            ) : filtered.length === 0 ? (
              <div className="pts-empty-state">
                <span className="material-symbols-outlined">person_search</span>
                <h3>No patients found</h3>
                <p>
                  {q
                    ? `No patients match "${q}". Try a different search term.`
                    : filter !== "All"
                      ? `No patients with status "${filter}".`
                      : "Get started by adding your first patient."}
                </p>
                {!q && filter === "All" && (
                  <button
                    className="pts-empty-action"
                    onClick={() => navigate("/patients/new")}
                  >
                    <span className="material-symbols-outlined">
                      person_add
                    </span>
                    Add Patient
                  </button>
                )}
              </div>
            ) : (
              <>
                {paginated.map((p) => {
                  const isHighlighted =
                    showPatientGuide && p.id === newPatientId;
                  return (
                    <div
                      className="pts-card-new"
                      key={p.id}
                      onClick={() => {
                        if (showPatientGuide && p.id === newPatientId) {
                          // Navigate to billing guide when in guide mode
                          navigate(
                            `/patients/${p.id}/edit?tab=billing&guide=true`,
                          );
                        } else {
                          navigate(`/patients/${p.id}/edit`);
                        }
                      }}
                      style={{
                        position: isHighlighted ? "relative" : undefined,
                        zIndex: isHighlighted ? 1002 : undefined,
                        background: isHighlighted ? "#fffbeb" : undefined,
                        border: isHighlighted ? "2px solid #f59e0b" : undefined,
                        boxShadow: isHighlighted
                          ? "0 0 0 4px rgba(245, 158, 11, 0.1)"
                          : undefined,
                        animation: isHighlighted
                          ? "pulseGlow 2s ease-in-out infinite"
                          : undefined,
                      }}
                    >
                      <div
                        className="pts-card-new-stripe"
                        style={{ background: statusColor(p.status) }}
                      />
                      <div className="pts-card-new-content">
                        <div className="pts-card-new-header">
                          <div className="pts-card-new-patient">
                            <div className="pts-card-new-avatar">
                              <span>{initialFromName(p.name, "P")}</span>
                            </div>
                            <div className="pts-card-new-info">
                              <h4
                                className="pts-card-new-name"
                                title={p.name || "Unnamed Patient"}
                              >
                                {p.name && p.name.length > 25
                                  ? `${p.name.substring(0, 25)}...`
                                  : p.name || "Unnamed Patient"}
                              </h4>
                              <div className="pts-card-new-meta">
                                <span className="material-symbols-outlined">
                                  medical_information
                                </span>
                                <span
                                  title={
                                    p.condition ||
                                    p.reason ||
                                    p.info ||
                                    "No condition specified"
                                  }
                                >
                                  {(() => {
                                    const condition =
                                      p.condition ||
                                      p.reason ||
                                      p.info ||
                                      "No condition specified";
                                    return condition.length > 30
                                      ? `${condition.substring(0, 30)}...`
                                      : condition;
                                  })()}
                                </span>
                                {p.age && (
                                  <>
                                    <span className="pts-card-new-separator">
                                      •
                                    </span>
                                    <span>{p.age} yrs</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="pts-card-new-status-wrapper">
                            {p.status && (
                              <span
                                className="pts-card-new-status"
                                style={{ color: statusColor(p.status) }}
                              >
                                {p.status}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {totalPages > 1 && (
                  <div className="pts-pagination">
                    <button
                      className="pts-pagination-btn"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <span className="material-symbols-outlined">
                        chevron_left
                      </span>
                      Previous
                    </button>
                    <div className="pts-pagination-info">
                      Page {currentPage} of {totalPages}
                      <span className="pts-pagination-count">
                        ({totalItems} total)
                      </span>
                    </div>
                    <button
                      className="pts-pagination-btn"
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <span className="material-symbols-outlined">
                        chevron_right
                      </span>
                    </button>
                  </div>
                )}

                <div className="pts-list-spacer" />
              </>
            )}
          </main>
        </div>
      </div>

      <FloatingActionButton
        onClick={() => navigate("/patients/new")}
        ariaLabel="Add Patient"
      />

      <nav className="appts-bottom-nav">
        <button className="appts-nav-item" onClick={() => navigate("/doctor")}>
          <span className="material-symbols-outlined">home</span>
          <span>Home</span>
        </button>
        <button
          className="appts-nav-item"
          onClick={() => navigate("/appointments")}
        >
          <span className="material-symbols-outlined">calendar_month</span>
          <span>Appointments</span>
        </button>
        <button
          className="appts-nav-item active"
          onClick={() => navigate("/patients")}
        >
          <span className="material-symbols-outlined fill-current">groups</span>
          <span>Patients</span>
        </button>
        <button className="appts-nav-item" onClick={() => navigate("/reports")}>
          <span className="material-symbols-outlined fill-current">
            analytics
          </span>
          <span>Reports</span>
        </button>
      </nav>

      {/* Patient Guide Overlay */}
      {showPatientGuide && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1001,
            background: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "white",
              borderRadius: "16px",
              padding: "24px",
              maxWidth: "500px",
              width: "90%",
              boxShadow:
                "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
              zIndex: 1003,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "12px",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ color: "white", fontSize: "24px" }}
                >
                  person_add
                </span>
              </div>
              <div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: "700",
                    color: "#1f2937",
                  }}
                >
                  Patient Added Successfully!
                </h3>
                <p
                  style={{
                    margin: "4px 0 0",
                    fontSize: "14px",
                    color: "#6b7280",
                    fontWeight: "500",
                  }}
                >
                  Now let's add their billing details
                </p>
              </div>
            </div>

            <div
              style={{
                background: "#f0fdf4",
                border: "1px solid #86efac",
                borderRadius: "12px",
                padding: "16px",
                marginBottom: "20px",
              }}
            >
              <div
                style={{ display: "flex", gap: "12px", alignItems: "start" }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ color: "#16a34a", fontSize: "24px" }}
                >
                  lightbulb
                </span>
                <div style={{ flex: 1 }}>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      color: "#166534",
                      fontWeight: "600",
                      lineHeight: "1.5",
                    }}
                  >
                    Click on the highlighted patient card above to open their
                    profile and add billing information.
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                setShowPatientGuide(false);
                setNewPatientId(null);
                // Clear URL params
                navigate("/patients", { replace: true });
              }}
              style={{
                width: "100%",
                padding: "12px",
                background: "#6366f1",
                color: "white",
                border: "none",
                borderRadius: "12px",
                fontSize: "15px",
                fontWeight: "700",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "20px" }}
              >
                close
              </span>
              Skip Guide
            </button>
          </div>
        </div>
      )}

      {/* Click={() => navigate("/patients/new")}
        aria-label="Add Patient"
      >
        <span className="material-symbols-outlined">add</span>
      </button>

      <nav className="appts-bottom-nav">
        <button className="appts-nav-item" onClick={() => navigate("/doctor")}>
          <span className="material-symbols-outlined">home</span>
          <span>Home</span>
        </button>
        <button
          className="appts-nav-item"
          onClick={() => navigate("/appointments")}
        >
          <span className="material-symbols-outlined">calendar_month</span>
          <span>Appointments</span>
        </button>
        <button
          className="appts-nav-item active"
          onClick={() => navigate("/patients")}
        >
          <span className="material-symbols-outlined fill-current">groups</span>
          <span>Patients</span>
        </button>
        <button
          className="appts-nav-item"
          onClick={() => navigate("/reports")}
        >
          <span className="material-symbols-outlined fill-current">analytics</span>
          <span>Reports</span>
        </button>
      </nav>

      {/* Form Link Modal */}
      {showFormLinkModal && (
        <div
          className="settings-modal"
          onClick={() => setShowFormLinkModal(false)}
        >
          <div
            className="settings-modal-card"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "600px", width: "90%" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: "20px",
                  fontWeight: "800",
                  color: "var(--register-text)",
                }}
              >
                Patient Form Link
              </h3>
              <button
                onClick={() => setShowFormLinkModal(false)}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "24px", color: "var(--register-muted)" }}
                >
                  close
                </span>
              </button>
            </div>

            <p
              style={{
                margin: "0 0 20px",
                fontSize: "14px",
                color: "var(--register-muted)",
                fontWeight: "500",
              }}
            >
              Share this link with your patients to allow them to register
              themselves. The link can be used multiple times.
            </p>

            {!formLink ? (
              <div style={{ textAlign: "center", padding: "32px 0" }}>
                <span
                  className="material-symbols-outlined"
                  style={{
                    fontSize: "48px",
                    color: "var(--register-muted)",
                    marginBottom: "12px",
                    display: "block",
                  }}
                >
                  link_off
                </span>
                <p
                  style={{
                    margin: 0,
                    fontSize: "14px",
                    color: "var(--register-muted)",
                    fontWeight: "600",
                  }}
                >
                  No form link generated yet
                </p>
              </div>
            ) : (
              <div style={{ marginBottom: "20px" }}>
                <div
                  style={{
                    border: "1px solid var(--register-border)",
                    borderRadius: "12px",
                    padding: "16px",
                    background: "var(--register-bg)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: "12px",
                      marginBottom: "12px",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        <span
                          className="material-symbols-outlined"
                          style={{
                            fontSize: "18px",
                            color: "var(--register-primary)",
                          }}
                        >
                          link
                        </span>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "12px",
                            color: "var(--register-muted)",
                            fontWeight: "600",
                          }}
                        >
                          Created{" "}
                          {formLink.createdAt?.toMillis
                            ? new Date(
                                formLink.createdAt.toMillis(),
                              ).toLocaleDateString()
                            : "Recently"}
                        </p>
                      </div>
                      <div
                        style={{
                          background: "var(--register-input-bg)",
                          borderRadius: "8px",
                          padding: "10px 12px",
                          fontSize: "13px",
                          fontFamily: "monospace",
                          color: "var(--register-text)",
                          wordBreak: "break-all",
                          border: "1px solid var(--register-border)",
                        }}
                      >
                        {getFormLinkUrl()}
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        fontSize: "12px",
                        color: "var(--register-muted)",
                        fontWeight: "600",
                      }}
                    >
                      Used {formLink.usageCount || 0} time
                      {formLink.usageCount !== 1 ? "s" : ""}
                    </p>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={shareFormLink}
                        style={{
                          background: "var(--register-primary)",
                          color: "white",
                          border: "none",
                          borderRadius: "8px",
                          padding: "8px 16px",
                          fontSize: "13px",
                          fontWeight: "700",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          transition: "all 0.2s",
                        }}
                        title="Share via native apps"
                      >
                        <span
                          className="material-symbols-outlined"
                          style={{ fontSize: "16px" }}
                        >
                          share
                        </span>
                        Share
                      </button>
                      <button
                        onClick={copyFormLink}
                        style={{
                          background: copiedLink
                            ? "var(--register-primary)"
                            : "var(--register-input-bg)",
                          color: copiedLink ? "white" : "var(--register-text)",
                          border: "none",
                          borderRadius: "8px",
                          padding: "8px 16px",
                          fontSize: "13px",
                          fontWeight: "700",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          transition: "all 0.2s",
                        }}
                        title="Copy link to clipboard"
                      >
                        <span
                          className="material-symbols-outlined"
                          style={{ fontSize: "16px" }}
                        >
                          {copiedLink ? "check" : "content_copy"}
                        </span>
                        {copiedLink ? "Copied!" : "Copy"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div
              style={{
                marginTop: "20px",
                paddingTop: "20px",
                borderTop: "1px solid var(--register-border)",
              }}
            >
              <button
                onClick={formLink ? createNewFormLink : generateFormLink}
                disabled={generatingLink}
                className="register-cta"
                style={{ width: "100%" }}
              >
                {generatingLink ? (
                  <>
                    <span
                      className="loading-spinner"
                      style={{
                        width: "16px",
                        height: "16px",
                        borderWidth: "2px",
                      }}
                    ></span>
                    {formLink ? "Creating..." : "Generating..."}
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined">
                      {formLink ? "refresh" : "add"}
                    </span>
                    {formLink ? "Create New Link" : "Generate Link"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PatientsPage;
